import React, { useCallback, useEffect, useRef, useState } from "react";

export const PALETTE = {
    red: "#FF7378",
    accent: "#FF5A5F",
    blue: "#3b5fd4",
    purple: "#7c3aed",
    green: "#16a34a",
    amber: "#d97706",
    sky: "#5FA4F7",
    rose: "#E47186",
    grey: "#9097ac",
};

export const SERIES_COLORS = [
    PALETTE.red,
    PALETTE.blue,
    PALETTE.purple,
    PALETTE.green,
    PALETTE.amber,
    PALETTE.sky,
    PALETTE.rose,
    PALETTE.grey,
];

const INK = "#1a1a2e";
const INK_MUTED = "#69708A";
const INK_FAINT = "#9097ac";
const SURFACE = "#fff";
const TRACK = "#F0F2F7";

export const formatCurrency = (value) => {
    const amount = Math.round(Number(value) || 0);
    return `Rs ${amount.toLocaleString("en-IN")}`;
};

export const formatCompactCurrency = (value) => {
    const amount = Number(value) || 0;
    if (Math.abs(amount) >= 1000) {
        return `Rs ${(amount / 1000).toFixed(1)}k`;
    }
    return formatCurrency(amount);
};

export const formatPercent = (value, digits = 0) => `${(Number(value) || 0).toFixed(digits)}%`;

export const formatMinutes = (value) => `${Math.round(Number(value) || 0)} min`;

export const formatCount = (value) => `${Math.round(Number(value) || 0)}`;

export function Card({ title, extra, children }) {
    return (
        <div
            style={{
                background: SURFACE,
                borderRadius: 14,
                padding: "18px 20px",
                border: "1.5px solid #ECEEF5",
                boxShadow: "0 2px 10px -5px rgba(40,40,80,.09)",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 15,
                    gap: 12,
                }}
            >
                <div style={{ fontSize: 13.5, fontWeight: 700, color: INK }}>{title}</div>
                {extra && <div style={{ flex: "0 0 auto" }}>{extra}</div>}
            </div>
            {children}
        </div>
    );
}

function Tooltip({ title, rows, x, y, flip, placement = "side", align = "middle", glide }) {
    const shiftX =
        placement === "side" ? `calc(${x}px + ${flip ? "-100% - 12px" : "12px"})` : `${x}px`;
    const shiftY =
        placement === "above"
            ? `calc(${y}px - 100% - 8px)`
            : placement === "below"
              ? `calc(${y}px + 8px)`
              : align === "top"
                ? `${y}px`
                : align === "bottom"
                  ? `calc(${y}px - 100%)`
                  : `calc(${y}px - 50%)`;

    return (
        <div
            role="tooltip"
            style={{
                position: "absolute",
                left: 0,
                top: 0,
                transform: `translate(${shiftX}, ${shiftY})`,
                transition: glide ? "transform .13s cubic-bezier(.22,.61,.36,1)" : "none",
                background: SURFACE,
                border: "1px solid #E4E7EF",
                borderRadius: 9,
                boxShadow: "0 6px 22px -8px rgba(40,40,80,.35)",
                padding: "8px 11px",
                pointerEvents: "none",
                zIndex: 20,
                minWidth: 108,
                whiteSpace: "nowrap",
            }}
        >
            {title && (
                <div style={{ fontSize: 10.5, color: INK_FAINT, fontWeight: 600, marginBottom: 5 }}>
                    {title}
                </div>
            )}
            {rows.map((row, index) => (
                <div
                    key={index}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginTop: index > 0 ? 4 : 0,
                    }}
                >
                    {row.color && (
                        <span
                            style={{
                                width: 10,
                                height: 2,
                                borderRadius: 2,
                                background: row.color,
                                flex: "0 0 auto",
                            }}
                        />
                    )}
                    <span style={{ fontSize: 13, fontWeight: 700, color: INK }}>{row.value}</span>
                    <span style={{ fontSize: 11, color: INK_MUTED, marginLeft: "auto" }}>{row.label}</span>
                </div>
            ))}
        </div>
    );
}

export function Bar({ value, max, color = PALETTE.red, height = 7, active }) {
    const pct = max > 0 ? Math.min(100, ((Number(value) || 0) / max) * 100) : 0;
    return (
        <div
            style={{
                flex: 1,
                height,
                background: TRACK,
                borderRadius: 4,
                overflow: "hidden",
                minWidth: 0,
            }}
        >
            <div
                style={{
                    height: "100%",
                    width: `${pct}%`,
                    background: color,
                    borderRadius: 4,
                    transition: "width .5s, filter .15s",
                    filter: active ? "brightness(1.12)" : "none",
                }}
            />
        </div>
    );
}

function useHoverTooltip() {
    const ref = useRef(null);
    const frame = useRef(0);
    const [tip, setTip] = useState(null);

    const show = useCallback((event, payload) => {
        const container = ref.current;
        if (!container) return;
        const clientX = event.clientX;
        if (frame.current) return;
        frame.current = requestAnimationFrame(() => {
            frame.current = 0;
            const bounds = container.getBoundingClientRect();
            if (payload.placement && payload.placement !== "side") {
                setTip({ ...payload, x: 0, y: bounds.height });
                return;
            }
            const x = clientX - bounds.left;
            setTip({ ...payload, x, y: bounds.height / 2, flip: x > bounds.width * 0.6 });
        });
    }, []);

    const showAt = useCallback((payload) => {
        const container = ref.current;
        if (!container) return;
        const bounds = container.getBoundingClientRect();
        if (payload.placement && payload.placement !== "side") {
            setTip({ ...payload, x: 0, y: bounds.height });
            return;
        }
        setTip({ ...payload, x: bounds.width * 0.5, y: bounds.height / 2, flip: true });
    }, []);

    const hide = useCallback(() => {
        if (frame.current) {
            cancelAnimationFrame(frame.current);
            frame.current = 0;
        }
        setTip(null);
    }, []);

    return { ref, tip, show, showAt, hide };
}

export function BarRow({ label, value, max, color, display, labelWidth = 132, details, trailing, displayWidth = 46 }) {
    const { ref, tip, show, showAt, hide } = useHoverTooltip();

    const rows = details && details.length > 0 ? details : [{ color, label, value: display }];

    return (
        <div
            ref={ref}
            tabIndex={0}
            onPointerMove={(event) => show(event, { title: label, rows })}
            onPointerLeave={hide}
            onFocus={() => showAt({ title: label, rows })}
            onBlur={hide}
            style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "3px 4px",
                margin: "-3px -4px",
                borderRadius: 6,
                outline: "none",
                cursor: "default",
                background: tip ? "#F8F9FC" : "transparent",
                transition: "background .15s",
            }}
        >
            <span
                style={{
                    fontSize: 11.5,
                    color: INK_MUTED,
                    width: labelWidth,
                    flex: `0 0 ${labelWidth}px`,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
                title={label}
            >
                {label}
            </span>
            <Bar value={value} max={max} color={color} active={Boolean(tip)} />
            <span
                style={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    color: INK,
                    minWidth: displayWidth,
                    textAlign: "right",
                }}
            >
                {display}
            </span>
            {trailing}
            {tip && <Tooltip {...tip} />}
        </div>
    );
}

export function LineChart({ data, lines, height = 130, labelKey = "label" }) {
    const containerRef = useRef(null);
    const svgRef = useRef(null);
    const frame = useRef(0);
    const [box, setBox] = useState(0);
    const [activeIndex, setActiveIndex] = useState(null);
    const [anchor, setAnchor] = useState(0);

    useEffect(() => {
        const node = containerRef.current;
        if (!node) return undefined;
        const measure = () => setBox(node.getBoundingClientRect().width);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    useEffect(
        () => () => {
            if (frame.current) cancelAnimationFrame(frame.current);
        },
        [],
    );

    const width = box || 500;
    const pad = { top: 10, right: 12, bottom: 24, left: 46 };
    const chartWidth = Math.max(1, width - pad.left - pad.right);
    const chartHeight = height - pad.top - pad.bottom;
    const points = data?.length ?? 0;

    const values = (data ?? []).flatMap((row) => lines.map((line) => Number(row[line.key]) || 0));
    const maxValue = Math.max(...values, 0) * 1.15 || 1;

    const x = useCallback(
        (index) =>
            points <= 1 ? pad.left + chartWidth / 2 : pad.left + (index / (points - 1)) * chartWidth,
        [points, chartWidth, pad.left],
    );
    const y = (value) => pad.top + chartHeight - ((Number(value) || 0) / maxValue) * chartHeight;

    const handleMove = useCallback(
        (event) => {
            const svg = svgRef.current;
            if (!svg || points === 0) return;
            const clientX = event.clientX;
            if (frame.current) return;
            frame.current = requestAnimationFrame(() => {
                frame.current = 0;
                const bounds = svg.getBoundingClientRect();
                const localX = clientX - bounds.left;
                const span = points <= 1 ? 1 : chartWidth / (points - 1);
                const nearest = Math.max(
                    0,
                    Math.min(points - 1, Math.round((localX - pad.left) / span)),
                );
                setAnchor(nearest);
                setActiveIndex((current) => (current === nearest ? current : nearest));
            });
        },
        [points, chartWidth, pad.left],
    );

    const handleKey = (event) => {
        if (points === 0) return;
        if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
            event.preventDefault();
            const step = event.key === "ArrowRight" ? 1 : -1;
            setActiveIndex((current) => {
                const next = Math.max(0, Math.min(points - 1, (current === null ? 0 : current) + step));
                setAnchor(next);
                return next;
            });
        }
        if (event.key === "Escape") setActiveIndex(null);
    };

    if (!data || points === 0) {
        return <EmptyState message="No data for this period" height={height} />;
    }

    const ticks = [0, 0.33, 0.67, 1].map((tick) => maxValue * tick);
    const path = (key) =>
        data
            .map(
                (row, index) =>
                    `${index === 0 ? "M" : "L"}${x(index).toFixed(1)},${y(row[key]).toFixed(1)}`,
            )
            .join(" ");

    const labelStep = Math.max(1, Math.ceil((points * 46) / Math.max(chartWidth, 1)));
    const dotRadius = points > 40 ? 0 : 3;
    const activeRow = activeIndex === null ? null : data[activeIndex];
    const activeLow =
        activeRow === null
            ? true
            : lines.reduce((sum, line) => sum + y(activeRow[line.key]), 0) / lines.length >
              pad.top + chartHeight / 2;

    return (
        <div ref={containerRef} style={{ position: "relative" }}>
            <svg
                ref={svgRef}
                viewBox={`0 0 ${width} ${height}`}
                width={width}
                height={height}
                tabIndex={0}
                role="img"
                onPointerMove={handleMove}
                onPointerLeave={() => setActiveIndex(null)}
                onFocus={() => {
                    setAnchor(points - 1);
                    setActiveIndex(points - 1);
                }}
                onBlur={() => setActiveIndex(null)}
                onKeyDown={handleKey}
                style={{ display: "block", outline: "none", touchAction: "none" }}
            >
                {ticks.map((tick, index) => (
                    <g key={index}>
                        <line
                            x1={pad.left}
                            y1={y(tick)}
                            x2={width - pad.right}
                            y2={y(tick)}
                            stroke={TRACK}
                            strokeWidth="1"
                        />
                        <text x={pad.left - 5} y={y(tick) + 4} textAnchor="end" fontSize="8.5" fill={INK_FAINT}>
                            {tick >= 1000 ? `${(tick / 1000).toFixed(0)}k` : tick.toFixed(0)}
                        </text>
                    </g>
                ))}

                {data.map((row, index) =>
                    index % labelStep === 0 ? (
                        <text
                            key={index}
                            x={x(index)}
                            y={height - 4}
                            textAnchor="middle"
                            fontSize="8.5"
                            fill={INK_FAINT}
                        >
                            {row[labelKey]}
                        </text>
                    ) : null,
                )}

                <g
                    style={{
                        transform: `translateX(${x(Math.min(anchor, Math.max(points - 1, 0)))}px)`,
                        transition:
                            "transform .13s cubic-bezier(.22,.61,.36,1), opacity .13s ease-out",
                        opacity: activeIndex === null ? 0 : 1,
                    }}
                >
                    <line
                        x1={0}
                        y1={pad.top}
                        x2={0}
                        y2={pad.top + chartHeight}
                        stroke="#C9CEDD"
                        strokeWidth="1"
                        strokeDasharray="3 3"
                    />
                </g>

                {lines.map((line) => (
                    <g key={line.key}>
                        <path
                            d={path(line.key)}
                            fill="none"
                            stroke={line.color}
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        {dotRadius > 0 &&
                            data.map((row, index) => (
                                <circle
                                    key={index}
                                    cx={x(index)}
                                    cy={y(row[line.key])}
                                    r={dotRadius}
                                    fill={line.color}
                                />
                            ))}
                    </g>
                ))}

                {activeRow &&
                    lines.map((line) => (
                        <circle
                            key={`active-${line.key}`}
                            cx={x(activeIndex)}
                            cy={y(activeRow[line.key])}
                            r={4.5}
                            fill={line.color}
                            stroke={SURFACE}
                            strokeWidth={2}
                            style={{ transition: "cx .13s cubic-bezier(.22,.61,.36,1), cy .13s cubic-bezier(.22,.61,.36,1)" }}
                        />
                    ))}
            </svg>

            {activeRow && (
                <Tooltip
                    glide
                    title={activeRow[labelKey]}
                    x={x(activeIndex)}
                    y={activeLow ? pad.top : pad.top + chartHeight}
                    align={activeLow ? "top" : "bottom"}
                    flip={x(activeIndex) > width * 0.58}
                    rows={lines.map((line) => ({
                        color: line.color,
                        label: line.label ?? line.key,
                        value: (line.format ?? formatCount)(activeRow[line.key]),
                    }))}
                />
            )}
        </div>
    );
}

export function Donut({ segments, size = 100 }) {
    const { ref, tip, show, showAt, hide } = useHoverTooltip();
    const radius = 36;
    const circumference = 2 * Math.PI * radius;
    const total = segments.reduce((sum, segment) => sum + (Number(segment.value) || 0), 0);

    if (total <= 0) {
        return (
            <svg viewBox="0 0 100 100" width={size} height={size} style={{ flex: "0 0 auto" }}>
                <circle cx="50" cy="50" r={radius} fill="none" stroke={TRACK} strokeWidth="16" />
            </svg>
        );
    }

    let cumulative = 0;
    const drawn = segments.map((segment) => {
        const amount = Number(segment.value) || 0;
        const fraction = amount / total;
        const entry = {
            ...segment,
            amount,
            dash: fraction * circumference,
            offset: circumference - (cumulative / total) * circumference,
            share: fraction * 100,
        };
        cumulative += amount;
        return entry;
    });

    return (
        <div ref={ref} style={{ position: "relative", flex: "0 0 auto", lineHeight: 0 }}>
            <svg viewBox="0 0 100 100" width={size} height={size}>
                <circle cx="50" cy="50" r={radius} fill="none" stroke={TRACK} strokeWidth="16" />
                {drawn.map((segment, index) => (
                    <circle
                        key={index}
                        cx="50"
                        cy="50"
                        r={radius}
                        fill="none"
                        stroke={segment.color}
                        strokeWidth={tip?.index === index ? 19 : 16}
                        strokeDasharray={`${segment.dash} ${circumference - segment.dash}`}
                        strokeDashoffset={segment.offset}
                        tabIndex={segment.label ? 0 : -1}
                        onPointerMove={(event) =>
                            segment.label &&
                            show(event, {
                                index,
                                placement: "below",
                                rows: [
                                    {
                                        color: segment.color,
                                        label: segment.label,
                                        value: `${segment.amount} · ${formatPercent(segment.share)}`,
                                    },
                                ],
                            })
                        }
                        onPointerLeave={hide}
                        onFocus={() =>
                            segment.label &&
                            showAt({
                                index,
                                placement: "below",
                                rows: [
                                    {
                                        color: segment.color,
                                        label: segment.label,
                                        value: `${segment.amount} · ${formatPercent(segment.share)}`,
                                    },
                                ],
                            })
                        }
                        onBlur={hide}
                        style={{
                            transform: "rotate(-90deg)",
                            transformOrigin: "50% 50%",
                            transition: "stroke-width .15s",
                            outline: "none",
                            cursor: segment.label ? "default" : "auto",
                        }}
                    />
                ))}
            </svg>
            {tip && <Tooltip {...tip} />}
        </div>
    );
}

export function LegendDot({ color, label }) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 10, height: 3, borderRadius: 2, background: color }} />
            <span style={{ fontSize: 10.5, color: INK_FAINT }}>{label}</span>
        </div>
    );
}

export function Stars({ rating }) {
    const rounded = Math.round(Number(rating) || 0);
    return (
        <span style={{ display: "inline-flex", gap: 1 }}>
            {[1, 2, 3, 4, 5].map((star) => (
                <svg key={star} width="11" height="11" viewBox="0 0 24 24">
                    <path
                        d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                        fill={star <= rounded ? "#f59e0b" : "#E4E7EF"}
                    />
                </svg>
            ))}
        </span>
    );
}

export function EmptyState({ message, height = 120 }) {
    return (
        <div
            style={{
                height,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: INK_FAINT,
                fontSize: 12.5,
            }}
        >
            {message}
        </div>
    );
}

export function LaunchingSoon() {
    return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 340 }}>
            <div
                style={{
                    background: SURFACE,
                    borderRadius: 14,
                    border: "1.5px dashed #D8DCE8",
                    padding: "44px 52px",
                    textAlign: "center",
                    maxWidth: 520,
                }}
            >
                <div style={{ fontSize: 19, fontWeight: 700, color: INK }}>Launching Soon</div>
            </div>
        </div>
    );
}

export function DataTable({ columns, rows, emptyMessage }) {
    const template = columns.map((column) => column.width || "1fr").join(" ");

    return (
        <div
            style={{
                background: SURFACE,
                borderRadius: 14,
                border: "1.5px solid #ECEEF5",
                boxShadow: "0 2px 10px -5px rgba(40,40,80,.09)",
                overflow: "hidden",
            }}
        >
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: template,
                    padding: "11px 20px",
                    borderBottom: "2px solid #F0F2F7",
                    background: "#F8F9FC",
                }}
            >
                {columns.map((column, index) => (
                    <div
                        key={column.key}
                        style={{
                            fontSize: 10.5,
                            fontWeight: 700,
                            color: INK_FAINT,
                            letterSpacing: 0.4,
                            textAlign: index === 0 ? "left" : column.align || "center",
                        }}
                    >
                        {column.label.toUpperCase()}
                    </div>
                ))}
            </div>
            {rows.length === 0 && (
                <div style={{ textAlign: "center", padding: 40, color: INK_FAINT, fontSize: 13 }}>
                    {emptyMessage}
                </div>
            )}
            {rows.map((row, rowIndex) => (
                <div
                    key={row.key ?? rowIndex}
                    style={{
                        display: "grid",
                        gridTemplateColumns: template,
                        padding: "13px 20px",
                        borderBottom: rowIndex < rows.length - 1 ? "1px solid #F4F5F9" : "none",
                        background: rowIndex % 2 === 0 ? SURFACE : "#FAFBFD",
                        alignItems: "center",
                    }}
                >
                    {columns.map((column, index) => (
                        <div
                            key={column.key}
                            style={{ textAlign: index === 0 ? "left" : column.align || "center", minWidth: 0 }}
                        >
                            {column.render(row)}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}
